import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  TextField,
} from "@mui/material";
import { CONTACT_ROLES, CONTACT_ROLE_LABELS } from "@/api/labels";
import type { ContactCreate, ContactRole } from "@/api/types";

export interface ContactDraft {
  role: ContactRole;
  name: string;
  email: string;
  phone: string;
  title: string;
  notes: string;
}

export const EMPTY_CONTACT_DRAFT: ContactDraft = {
  role: "it",
  name: "",
  email: "",
  phone: "",
  title: "",
  notes: "",
};

export function contactDraftToPayload(d: ContactDraft): ContactCreate {
  return {
    role: d.role,
    name: d.name.trim(),
    email: d.email.trim() || null,
    phone: d.phone.trim() || null,
    title: d.title.trim() || null,
    notes: d.notes.trim() || null,
  };
}

interface Props {
  initial: ContactDraft;
  submitLabel: string;
  onSubmit: (d: ContactDraft) => Promise<void>;
  onCancel: () => void;
}

export default function ContactForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: Props) {
  const [draft, setDraft] = useState<ContactDraft>(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Intentionally rendered as a <div>, not a <form>. The component is
  // also embedded inside SiteForm's <form>, and nested <form> elements
  // are invalid HTML — the browser silently closes the outer one. We
  // submit through the button's onClick instead.
  const submit = async () => {
    setError(null);
    if (!draft.name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Stop Enter inside an input from bubbling up to a parent <form>
  // (SiteForm wraps this). Submit the contact form instead.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const isTextarea = target.tagName === "TEXTAREA";
    if (e.key === "Enter" && !isTextarea) {
      e.preventDefault();
      e.stopPropagation();
      void submit();
    }
  };

  return (
    <Box
      onKeyDown={onKeyDown}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 2,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "160px 1fr 1fr" },
          gap: 1.5,
        }}
      >
        <TextField
          size="small"
          select
          label="Role"
          value={draft.role}
          onChange={(e) =>
            setDraft({ ...draft, role: e.target.value as ContactRole })
          }
        >
          {CONTACT_ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {CONTACT_ROLE_LABELS[r]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          required
          label="Name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <TextField
          size="small"
          label="Title"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <TextField
          size="small"
          label="Email"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          sx={{ gridColumn: { md: "1 / 3" } }}
        />
        <TextField
          size="small"
          label="Phone"
          value={draft.phone}
          onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
        />
        <TextField
          size="small"
          label="Notes"
          multiline
          minRows={2}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          sx={{ gridColumn: { md: "1 / 4" } }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          mt: 1.5,
        }}
      >
        <Button size="small" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          type="button"
          variant="contained"
          disabled={saving}
          onClick={submit}
        >
          {saving ? "Saving…" : submitLabel}
        </Button>
      </Box>
    </Box>
  );
}
